// ═══════════════════════════════════════════════════════════════════════════
// TrueSelfReportTab.js — Layer 3: "Your True Self — A Vedic Portrait" (₹99)
// Paid one-time report generated from real birth chart data.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// Matches JyotishPage.js's JT token system — white, one accent, plain Inter.
const RT = {
  page:     "#ffffff",
  card:     "#ffffff",
  cardWarm: "#fafafa",
  border:   "#e2e2e2",
  shadow:   "0 1px 3px rgba(0,0,0,0.08)",
  font:     "'Inter',sans-serif",
  text:     "#1a1a1a",
  textDim:  "#5a5a5a",
  accent:   "#c2560f",
};

const SECTION_ICONS = {
  "Your Cosmic Identity":    "🌌",
  "Your Soul's Signature":   "🔮",
  "Your Natural Strengths":  "✨",
  "Your Growth Edge":        "🌱",
  "Your Dharmic Path":       "🔱",
};

function getSectionIcon(title) {
  for (const [key, icon] of Object.entries(SECTION_ICONS)) {
    if (title.includes(key)) return icon;
  }
  if (title.toLowerCase().includes("birth star") ||
      title.toLowerCase().includes("nakshatra")) return "⭐";
  return "🪐";
}

// ── Loading shimmer ──────────────────────────────────────────────────────────
function ReportShimmer() {
  const shimmer = {
    background: "linear-gradient(90deg, #f0f0f0 25%, #e2e2e2 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.6s infinite",
    borderRadius: 8,
  };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20, padding:"8px 0" }}>
      {[1,2,3,4,5,6].map(i => (
        <div key={i} style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ ...shimmer, height:20, width:`${40 + i * 8}%` }} />
          <div style={{ ...shimmer, height:14, width:"100%" }} />
          <div style={{ ...shimmer, height:14, width:"92%" }} />
          <div style={{ ...shimmer, height:14, width:"96%" }} />
        </div>
      ))}
      <p style={{ textAlign:"center", color:RT.textDim, fontSize:"0.95rem", marginTop:8 }}>
        Reading your stars... this takes about 15 seconds
      </p>
    </div>
  );
}

// ── Name entry prompt ────────────────────────────────────────────────────────
function NamePrompt({ onSubmit }) {
  const [name, setName] = useState("");
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16, alignItems:"center",
                  padding:"24px 0" }}>
      <div style={{ fontSize:"2.5rem" }}>🔱</div>
      <p style={{ color:RT.text, textAlign:"center", fontSize:"1.05rem", margin:0,
                  lineHeight:1.6 }}>
        Your report opens with your name.<br/>
        <span style={{ color:RT.textDim, fontSize:"0.95rem" }}>
          "Prepared for [Name] · Born [Date] · [Place]"
        </span>
      </p>
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === "Enter" && name.trim() && onSubmit(name.trim())}
        placeholder="Enter your name"
        style={{ width:"100%", maxWidth:300, padding:"12px 16px",
                 background:"#ffffff", border:`1px solid ${RT.border}`,
                 borderRadius:10, color:RT.text, fontSize:"1rem",
                 outline:"none", textAlign:"center" }}
      />
      <button
        disabled={!name.trim()}
        onClick={() => onSubmit(name.trim())}
        style={{ padding:"12px 32px", borderRadius:24,
                 background: name.trim() ? RT.accent : "#f0f0f0",
                 border:"none", color: name.trim() ? "#fff" : RT.textDim,
                 fontSize:"1rem", fontWeight:700, cursor: name.trim() ? "pointer":"default",
                 transition:"all 0.2s" }}>
        Generate My Report
      </button>
    </div>
  );
}

// ── Single report section card ───────────────────────────────────────────────
function SectionCard({ section, index, expanded, onToggle }) {
  const icon = getSectionIcon(section.title);
  const isBirthStar = section.title.toLowerCase().includes("birth star") ||
                      section.title.toLowerCase().includes("nakshatra");

  return (
    <div
      onClick={onToggle}
      style={{ borderRadius:16, overflow:"hidden", cursor:"pointer",
               border: `1px solid ${RT.border}`, background: RT.card,
               transition:"all 0.2s",
               boxShadow: expanded ? RT.shadow : "none",
               animation:`fadeInUp 0.4s ease ${index * 0.07}s both` }}>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:14, padding:"18px 20px" }}>
        <span style={{ fontSize:"1.6rem", flexShrink:0 }}>{icon}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ color: RT.text, fontWeight:700, fontSize:"1.1rem",
                        fontFamily:RT.font, lineHeight:1.3 }}>
            {section.title}
          </div>
        </div>
        <div style={{ color: RT.accent,
                       fontSize:"1.3rem",
                       transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                       transition:"transform 0.2s", flexShrink:0 }}>›</div>
      </div>

      {/* Body */}
      {expanded && (
        <div style={{ padding:"0 20px 22px", borderTop: `1px solid ${RT.border}` }}>
          {isBirthStar && (
            <div style={{ display:"inline-block", marginTop:14, marginBottom:14,
                          padding:"6px 14px", borderRadius:20,
                          background:"rgba(245,158,11,0.1)",
                          border:"1px solid rgba(245,158,11,0.3)",
                          color:"#a16207", fontSize:"0.95rem", fontWeight:600 }}>
              ⭐ Your Janma Nakshatra
            </div>
          )}
          <p style={{ color: RT.text, lineHeight:1.85, fontSize:"1.15rem",
                      fontFamily:RT.font,
                      margin: isBirthStar ? "0" : "16px 0 0",
                      whiteSpace:"pre-line" }}>
            {section.body}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Report header banner ─────────────────────────────────────────────────────
function ReportHeader({ report }) {
  return (
    <div style={{ borderRadius:18, overflow:"hidden", marginBottom:22,
                  background: RT.cardWarm, border: `1px solid ${RT.border}`, padding:"22px 20px",
                  boxShadow: RT.shadow }}>
      {/* Top accent line */}
      <div style={{ height:3, background:RT.accent,
        width: 64, borderRadius:2, margin:"0 0 18px" }} />
      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
        <span style={{ fontSize:"2.5rem" }}>
          {report.character_emoji || "🔱"}
        </span>
        <div>
          <div style={{ color: RT.accent, fontSize:"0.95rem", fontWeight:700,
                        fontFamily:RT.font, marginBottom:"4px" }}>
            Your True Self — A Vedic Portrait
          </div>
          <div style={{ color: RT.text, fontWeight:700, fontSize:"1.3rem",
                        fontFamily:RT.font }}>
            {report.name}
          </div>
        </div>
      </div>

      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
        {[
          { label:"Archetype",  value: report.character },
          { label:"Birth Star", value: report.janma_nakshatra },
          { label:"Moon Rashi", value: report.moon_rashi },
          report.lagna_rashi && { label:"Lagna", value: report.lagna_rashi },
        ].filter(Boolean).map((item, i) => (
          <div key={i} style={{ padding:"7px 14px", borderRadius:20,
                                background: "#ffffff",
                                border: `1px solid ${RT.border}` }}>
            <span style={{ color: RT.textDim, fontSize:"0.95rem" }}>
              {item.label}:{" "}
            </span>
            <span style={{ color: RT.text, fontSize:"0.98rem", fontWeight:700 }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Share/save row ───────────────────────────────────────────────────────────
function ReportActions({ report, onExpandAll, allExpanded }) {
  const [copied, setCopied] = useState(false);

  const copyText = () => {
    const lines = [
      `Your True Self — A Vedic Portrait`,
      `Prepared for ${report.name} · ${report.dob} · ${report.birth_city}`,
      `Archetype: ${report.character} · Birth Star: ${report.janma_nakshatra}`,
      "",
      ...(report.sections || []).flatMap(s => [
        `── ${s.title} ──`,
        s.body,
        "",
      ]),
      "Generated by VedSanga · vedsanga.com",
    ];
    navigator.clipboard.writeText(lines.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const btnBase = { flex:1, padding:"11px 16px", borderRadius:24,
    background:"#ffffff", border:`1px solid ${RT.border}`,
    color:RT.accent, fontSize:"1rem", cursor:"pointer", fontFamily:RT.font, fontWeight:600,
    transition:"all 0.2s" };
  return (
    <div style={{ display:"flex", gap:10, marginBottom:22, flexWrap:"wrap" }}>
      <button onClick={onExpandAll} style={btnBase}>
        {allExpanded ? "Collapse All" : "Expand All"}
      </button>
      <button onClick={copyText}
        style={{ ...btnBase,
                 background: copied ? "rgba(34,197,94,0.08)" : "#ffffff",
                 borderColor: copied ? "rgba(34,197,94,0.4)" : RT.border,
                 color: copied ? "#15803d" : RT.accent }}>
        {copied ? "✓ Copied!" : "📋 Copy"}
      </button>
      <button onClick={() => {
        const text = `🔱 My Vedic Portrait on VedSanga\n\nI am ${report.character} · ${report.janma_nakshatra} nakshatra\n\nDiscover your Vedic portrait → vedsanga.com`;
        if (navigator.share) {
          navigator.share({ title:"My Vedic Portrait", text });
        } else {
          navigator.clipboard.writeText(text);
        }
      }}
        style={btnBase}>
        🔗 Share
      </button>
    </div>
  );
}

// ── Main exported component ──────────────────────────────────────────────────
export default function TrueSelfReportTab({ userId, noBirth, onNeedBirth, arcData }) {
  const [phase,       setPhase]       = useState("intro");
  const [report,      setReport]      = useState(null);
  const [expanded,    setExpanded]    = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [payOrder,    setPayOrder]    = useState(null);
  const [pendingName, setPendingName] = useState("");
  const [payLoading,  setPayLoading]  = useState(false);
  const [payError,    setPayError]    = useState("");
  const topRef = useRef(null);

  const loadRazorpay = () => new Promise(resolve => {
    if (document.getElementById("rzp-script")) { resolve(true); return; }
    const s = document.createElement("script");
    s.id = "rzp-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  // Auto-load if already purchased and cached
  useEffect(() => {
    if (!userId || noBirth) return;
    fetch(`${API}/jyotish/report/true-self/${encodeURIComponent(userId)}`)
      .then(async r => {
        if (r.status === 402) return null;
        return r.json();
      })
      .then(d => {
        if (d?.sections?.length > 0) {
          setReport(d);
          setExpanded({ 0: true, 1: true });
          setPhase("done");
        }
      })
      .catch(() => {});
  }, [userId]);

  const handleGenerate = async (name) => {
    setPhase("loading");
    setPendingName(name);
    topRef.current?.scrollIntoView({ behavior:"smooth" });
    try {
      const res = await fetch(
        `${API}/jyotish/report/true-self/${encodeURIComponent(userId)}?name=${encodeURIComponent(name)}`
      );
      if (res.status === 402) {
        const orderRes = await fetch(`${API}/jyotish/report/create-order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, report_type: "true_self" }),
        });
        const order = await orderRes.json();
        if (!order.order_id) { setPhase("error"); return; }
        setPayOrder(order);
        setPhase("payment");
        return;
      }
      const d = await res.json();
      if (d.error) { setPhase("error"); return; }
      setReport(d);
      setExpanded({ 0: true, 1: true });
      setPhase("done");
    } catch {
      setPhase("error");
    }
  };

  const handlePay = async () => {
    setPayLoading(true);
    setPayError("");
    const loaded = await loadRazorpay();
    if (!loaded) { setPayError("Could not load payment SDK."); setPayLoading(false); return; }
    const rzp = new window.Razorpay({
      key:         payOrder.key_id,
      amount:      payOrder.amount,
      currency:    payOrder.currency,
      order_id:    payOrder.order_id,
      name:        "VedSanga",
      description: "Your True Self Portrait",
      image:       "/logo192.png",
      theme:       { color: RT.accent },
      method:      { upi: true, card: true, netbanking: true, wallet: true },
      handler: async (response) => {
        try {
          const vRes = await fetch(`${API}/jyotish/report/verify-purchase`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id:             userId,
              report_type:         "true_self",
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            }),
          });
          const vData = await vRes.json();
          if (vData.ok) {
            handleGenerate(pendingName);
          } else {
            setPayError("Payment verified but access grant failed. Contact support.");
            setPhase("payment");
          }
        } catch {
          setPayError("Verification failed. Please contact support.");
          setPhase("payment");
        }
      },
      modal: { ondismiss: () => { setPhase("intro"); setPayLoading(false); } },
    });
    rzp.open();
    setPayLoading(false);
  };

  const toggleSection = (idx) => {
    setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleExpandAll = () => {
    if (allExpanded) {
      setExpanded({});
      setAllExpanded(false);
    } else {
      const all = {};
      (report?.sections || []).forEach((_, i) => { all[i] = true; });
      setExpanded(all);
      setAllExpanded(true);
    }
  };

  // Keep allExpanded in sync
  useEffect(() => {
    if (!report?.sections) return;
    setAllExpanded(report.sections.every((_, i) => expanded[i]));
  }, [expanded, report]);

  // ── No birth data ──
  if (noBirth) {
    return (
      <div style={{ textAlign:"center", padding:"40px 16px" }}>
        <div style={{ fontSize:"2.5rem", marginBottom:12 }}>🪐</div>
        <p style={{ color:RT.textDim, fontSize:"1rem", lineHeight:1.7 }}>
          Add your birth date and city to generate your Vedic portrait.
        </p>
        <button onClick={onNeedBirth}
          style={{ marginTop:12, padding:"10px 28px", borderRadius:24,
                   background:RT.accent,
                   border:"none", color:"#fff", fontSize:"1rem", fontWeight:600, cursor:"pointer" }}>
          Add Birth Details
        </button>
      </div>
    );
  }

  return (
    <div ref={topRef} style={{ display:"flex", flexDirection:"column", gap:0 }}>

      {/* ── Intro phase ── */}
      {phase === "intro" && (
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
          {/* Hero */}
          <div style={{ borderRadius:18, padding:"28px 22px", textAlign:"center",
                        background:RT.cardWarm,
                        border:`1px solid ${RT.border}`,
                        boxShadow:RT.shadow }}>
            <div style={{ height:3, width:64, background:RT.accent,
              borderRadius:2, margin:"0 auto 20px" }} />
            <div style={{ fontSize:"3rem", marginBottom:10 }}>🔱</div>
            <div style={{ fontFamily:RT.font, color:RT.text,
                          fontSize:"1.4rem", fontWeight:700, marginBottom:8 }}>
              Your True Self
            </div>
            <div style={{ color:RT.textDim, fontSize:"1.05rem", fontFamily:RT.font,
              fontStyle:"italic", lineHeight:1.7 }}>
              A Vedic Portrait — from your Janma Kundali
            </div>
          </div>

          {/* What's inside */}
          <div style={{ background:RT.card,
                        borderRadius:16, border:`1px solid ${RT.border}`, padding:"20px 18px" }}>
            <p style={{ color:RT.accent, fontSize:"0.95rem", fontWeight:700,
                        margin:"0 0 16px",
                        fontFamily:RT.font }}>
              What's inside
            </p>
            {[
              ["🌌", "Your Cosmic Identity",   "How your Lagna, Moon, and Sun work together"],
              ["⭐", "Your Birth Star",         `Deep profile of your Janma Nakshatra${arcData?.nakshatra ? ` — ${arcData.nakshatra}` : ""}`],
              ["🔮", "Your Soul's Signature",  "Atmakaraka (soul planet) + your strongest yogas"],
              ["✨", "Your Natural Strengths", "Where you move with grace and natural power"],
              ["🌱", "Your Growth Edge",        "What life is asking you to develop"],
              ["🔱", "Your Dharmic Path",       "Your purpose — grounded in a Gita verse"],
            ].map(([icon, title, desc], i) => (
              <div key={i} style={{ display:"flex", gap:14, alignItems:"flex-start",
                                    padding:"12px 0",
                                    borderBottom: i < 5 ? `1px solid ${RT.border}`:"none" }}>
                <span style={{ fontSize:"1.4rem", flexShrink:0, marginTop:2 }}>{icon}</span>
                <div>
                  <div style={{ color:RT.text, fontSize:"1.05rem", fontWeight:700,
                    fontFamily:RT.font, marginBottom:3 }}>
                    {title}
                  </div>
                  <div style={{ color:RT.textDim, fontSize:"0.98rem",
                    fontFamily:RT.font, lineHeight:1.6 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Price + CTA */}
          <div style={{ borderRadius:16, padding:"20px 18px",
                        background:RT.cardWarm,
                        border:`1px solid ${RT.border}`,
                        boxShadow:RT.shadow,
                        textAlign:"center" }}>
            <div style={{ color:RT.textDim, fontSize:"1rem", marginBottom:8,
              fontFamily:RT.font }}>
              One-time · Never expires · Based on your real birth chart
            </div>
            <div style={{ color:RT.accent, fontSize:"1.6rem", fontWeight:700,
                          fontFamily:RT.font, marginBottom:16 }}>
              ₹99
            </div>
            <button onClick={() => setPhase("name")}
              style={{ width:"100%", padding:"15px", borderRadius:24,
                       background:RT.accent,
                       border:"none", color:"#ffffff", fontSize:"1.05rem",
                       fontWeight:700, cursor:"pointer",
                       fontFamily:RT.font,
                       boxShadow:"0 4px 20px rgba(194,86,15,0.35)" }}>
              Generate My Portrait →
            </button>
            <p style={{ color:RT.textDim, fontSize:"0.95rem", margin:"10px 0 0",
              fontFamily:RT.font }}>
              Generated once from your actual Janma Kundali data
            </p>
          </div>
        </div>
      )}

      {/* ── Name entry ── */}
      {phase === "name" && (
        <NamePrompt onSubmit={handleGenerate} />
      )}

      {/* ── Loading ── */}
      {phase === "loading" && <ReportShimmer />}

      {/* ── Payment ── */}
      {phase === "payment" && payOrder && (
        <div style={{ display:"flex", flexDirection:"column", gap:16, padding:"8px 0" }}>
          <div style={{ borderRadius:18, padding:"28px 20px",
                        background:RT.cardWarm,
                        border:`1px solid ${RT.border}`, textAlign:"center" }}>
            <div style={{ fontSize:"2.4rem", marginBottom:12 }}>🔐</div>
            <div style={{ fontFamily:RT.font,
                          color:RT.text, fontSize:"1.1rem", fontWeight:700, marginBottom:6 }}>
              Your True Self Portrait
            </div>
            <div style={{ color:RT.textDim, fontSize:"0.98rem", marginBottom:20 }}>
              One-time purchase · Never expires · Permanent access
            </div>
            <div style={{ fontSize:"2.4rem", fontWeight:700,
                          fontFamily:RT.font,
                          color:RT.accent, marginBottom:24 }}>
              ₹{payOrder.amount / 100}
            </div>
            {payError && (
              <div style={{ color:"#b91c1c", fontSize:"0.95rem", marginBottom:14,
                            padding:"8px 12px", background:"rgba(239,68,68,0.06)", borderRadius:8 }}>
                {payError}
              </div>
            )}
            <button
              onClick={handlePay}
              disabled={payLoading}
              style={{ width:"100%", padding:"16px", borderRadius:24,
                       background: payLoading ? "#f0f0f0" : RT.accent,
                       border:"none", color: payLoading ? RT.textDim : "#fff",
                       fontSize:"1.05rem", fontWeight:700,
                       cursor: payLoading ? "default" : "pointer",
                       fontFamily:RT.font,
                       boxShadow: payLoading ? "none" : "0 4px 20px rgba(194,86,15,0.35)" }}>
              {payLoading ? "Opening payment..." : "Pay ₹99 — UPI / Card / NetBanking"}
            </button>
            <p style={{ color:RT.textDim, fontSize:"0.9rem", marginTop:12, marginBottom:0 }}>
              🔒 Secured by Razorpay · Report unlocked permanently after payment
            </p>
          </div>
          <button onClick={() => setPhase("intro")}
            style={{ background:"transparent", border:"none", color:RT.textDim,
                     fontSize:"0.95rem", cursor:"pointer", padding:"8px 0", textAlign:"center" }}>
            ← Go back
          </button>
        </div>
      )}

      {/* ── Error ── */}
      {phase === "error" && (
        <div style={{ textAlign:"center", padding:"40px 16px" }}>
          <div style={{ fontSize:"2rem", marginBottom:12 }}>⚠️</div>
          <p style={{ color:RT.textDim, fontSize:"1rem" }}>
            Something went wrong generating your report. Please try again.
          </p>
          <button onClick={() => setPhase("intro")}
            style={{ marginTop:12, padding:"10px 28px", borderRadius:24,
                     background:"#ffffff", border:`1px solid ${RT.border}`,
                     color:RT.text, fontSize:"1rem", cursor:"pointer" }}>
            Try Again
          </button>
        </div>
      )}

      {/* ── Done — show report ── */}
      {phase === "done" && report && (
        <div>
          <ReportHeader report={report} />
          <ReportActions
            report={report}
            onExpandAll={handleExpandAll}
            allExpanded={allExpanded}
          />
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {report.sections.map((section, idx) => (
              <SectionCard
                key={idx}
                section={section}
                index={idx}
                expanded={!!expanded[idx]}
                onToggle={() => toggleSection(idx)}
              />
            ))}
          </div>
          <div style={{ textAlign:"center", marginTop:24, marginBottom:8 }}>
            <button
              onClick={() => { setPhase("name"); setReport(null); }}
              style={{ padding:"8px 20px", borderRadius:20,
                       background:"transparent",
                       border:`1px solid ${RT.border}`,
                       color:RT.textDim, fontSize:"0.95rem", cursor:"pointer" }}>
              Regenerate with different name
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from { opacity:0; transform:translateY(16px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
